import {useEffect, useRef} from 'react'
import {UseFormGetValues, UseFormSetValue, UseFormTrigger} from 'react-hook-form'

import {Config} from '../../../types'
import {PRODUCTION_DAY_DEFAULTS} from '../utils/productionDayUtils'

/**
 * Calculates the production day end time based on start time and custom boundaries flag
 *
 * Rules:
 * - When custom boundaries disabled: always END_TIME (00:00)
 * - When custom boundaries enabled: end time equals start time
 */
export const calculateProductionDayEndTime = (
  startTime: string,
  useCustomBoundaries: string
): string => {
  if (useCustomBoundaries === 'true') {
    return startTime
  }

  return PRODUCTION_DAY_DEFAULTS.END_TIME
}

export const useProductionDayBoundaries = (
  getValues: UseFormGetValues<Config>,
  setValue: UseFormSetValue<Config>,
  trigger: UseFormTrigger<Config>,
  watchUseCustomBoundaries: string,
  watchStartTime: string
) => {
  const isInitialized = useRef(false)

  useEffect(() => {
    if (isInitialized.current) return

    const currentFlag = getValues('useCustomProductionDayBoundaries')
    const currentStartTime = getValues('productionDayStartTime')

    const startTime = currentStartTime || PRODUCTION_DAY_DEFAULTS.START_TIME
    if (!currentStartTime) {
      setValue('productionDayStartTime', startTime)
    }

    const endTime = calculateProductionDayEndTime(startTime, currentFlag)
    setValue('productionDayEndTime', endTime)

    isInitialized.current = true
    void trigger(['productionDayStartTime', 'productionDayEndTime'])
  }, [getValues, setValue, trigger])

  useEffect(() => {
    if (!isInitialized.current) return

    if (watchUseCustomBoundaries === 'false') {
      setValue('productionDayStartTime', PRODUCTION_DAY_DEFAULTS.START_TIME)
      setValue('productionDayEndTime', PRODUCTION_DAY_DEFAULTS.END_TIME)
    } else if (watchUseCustomBoundaries === 'true') {
      const startTime = watchStartTime || PRODUCTION_DAY_DEFAULTS.START_TIME
      const endTime = calculateProductionDayEndTime(startTime, 'true')
      setValue('productionDayEndTime', endTime)
    }

    void trigger(['productionDayStartTime', 'productionDayEndTime'])
  }, [watchUseCustomBoundaries, watchStartTime, setValue, trigger])
}
